<script lang="ts">
    import { toast } from "svelte-sonner";
    import { router } from "@inertiajs/svelte";
    import { useForm } from "@/lib/precognition.svelte";
    import Modal from "@/components/Modal.svelte";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import { Button } from "@/components/ui/button";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import Select from "@/components/Inputs/Select.svelte";
    import Checkbox from "@/components/Inputs/Checkbox.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import DateTime from "@/components/Inputs/DateTime.svelte";
    import RelatedSingle from "@/components/Inputs/RelatedSingle.svelte";
    import CustomerTable from "@/components/customers/CustomerTable.svelte";
    import type Blocks from "@/schemas/public/Blocks";
    import type { Field, CustomerType } from "@/types/types";
    import { Building2, User } from "lucide-svelte";
    import { parseFieldOptions } from "@/lib/fieldUtils";

    type Props = {
        show: boolean;
        blocks: (Blocks & { fields: Field[] })[];
        customerTypeOptions?: Record<string, string>;
        availableOrganisations?: { id: string; name: string }[];
        defaultType?: CustomerType;
        defaultOrganisationId?: string | null;
        initialValues?: Record<string, any>;
    };

    let {
        show = $bindable(),
        blocks = [],
        customerTypeOptions = { 'Contact': 'contact', 'Organisation': 'organisation' },
        availableOrganisations = [],
        defaultType = 'contact',
        defaultOrganisationId = null,
        initialValues = {}
    }: Props = $props();

    // Build initial form data from blocks
    function buildFormData() {
        let data: Record<string, any> = {
            type: initialValues.type ?? defaultType,
            organisation_id: initialValues.organisation_id ?? defaultOrganisationId,
        };

        for (const block of blocks) {
            for (const field of block.fields) {
                if (data[field.column_name] === undefined) {
                    // Use initial value if provided, otherwise null
                    data[field.column_name] = initialValues[field.column_name] ?? null;
                }
            }
        }
        return data;
    }

    let form = $state(useForm(buildFormData(), 'post', route('customers.store')));

    // Reinitialize form when modal opens with correct defaults
    $effect(() => {
        if (show) {
            const newData = buildFormData();
            Object.keys(newData).forEach(key => {
                $form[key] = newData[key];
            });
        }
    });

    const isOrganisation = $derived($form.type === 'organisation');
    const isContact = $derived($form.type === 'contact');

    // Organisation options for select
    const organisationOptions = $derived([
        { label: 'None', value: '' },
        ...availableOrganisations.map(org => ({ label: org.name, value: org.id }))
    ]);

    // Organisation selector state
    let isSelectOrgOpen = $state(false);
    let orgLabel = $state<string | null>(null);

    // Initialize orgLabel from availableOrganisations if defaultOrganisationId is set
    $effect(() => {
        if (defaultOrganisationId && availableOrganisations.length > 0) {
            const org = availableOrganisations.find(o => o.id === defaultOrganisationId);
            if (org) {
                orgLabel = org.name;
            }
        }
    });

    // Fields to hide completely
    const hiddenFields = ['type', 'created_at', 'updated_at', 'related_jobs'];

    // Determine which fields to show based on type
    function shouldShowField(field: Field): boolean {
        const columnName = field.column_name;

        // Always hide these fields
        if (hiddenFields.includes(columnName)) return false;

        // Show organisation_id only for contacts and when not pre-set
        if (columnName === 'organisation_id') return isContact && !defaultOrganisationId;

        // Hide first_name and last_name for organisations
        if ((columnName === 'first_name' || columnName === 'last_name') && isOrganisation) return false;

        // Hide organisation_name for contacts
        if (columnName === 'organisation_name' && isContact) return false;

        return true;
    }

    // Determine if field is required based on type
    function isFieldRequired(field: Field): boolean {
        const columnName = field.column_name;

        if (isOrganisation) {
            // Organisation requires organisation_name and email
            if (columnName === 'organisation_name' || columnName === 'email') return true;
            return false;
        }

        // Contact requires email (first_name or organisation_name via backend validation)
        if (columnName === 'email') return true;
        return field.required || false;
    }

    // Get label for field
    function getFieldLabel(field: Field): string {
        return field.label;
    }

    // Sort fields - for organisations, put organisation_name first
    function getSortedFields(fields: Field[]): Field[] {
        if (!isOrganisation) return fields;

        return [...fields].sort((a, b) => {
            if (a.column_name === 'organisation_name') return -1;
            if (b.column_name === 'organisation_name') return 1;
            return (a.sequence || 0) - (b.sequence || 0);
        });
    }

    function close() {
        show = false;
        $form.reset();
    }

    function submit(e: Event) {
        e.preventDefault();
        $form.processing = true;

        // If creating a contact for an organisation, stay on the current page
        const shouldStayOnPage = defaultOrganisationId !== null;

        $form.post(route('customers.store'), {
            preserveScroll: shouldStayOnPage,
            onSuccess: () => {
                close();
                toast.success(`${isOrganisation ? 'Organisation' : 'Contact'} created successfully!`);
                // If we created a contact for an organisation, navigate back to the organisation page
                if (shouldStayOnPage && defaultOrganisationId) {
                    router.visit(route('customers.show', { customer: defaultOrganisationId }));
                }
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error(`Error creating customer: ${message as string}`);
                }
            },
            onFinish: () => {
                $form.processing = false;
            }
        });
    }
</script>

{#if show}
    <Modal title={isOrganisation ? 'Add New Organisation' : 'Add New Contact'} onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                <!-- Type Selection (hidden when creating contact for an organisation) -->
                {#if !defaultOrganisationId}
                    <FormBlock label="Customer Type">
                        <div class="flex gap-4">
                            <button
                                type="button"
                                onclick={() => $form.type = 'contact'}
                                class="flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-colors {isContact ? 'border-primary bg-primary/10' : 'border-muted hover:border-muted-foreground/50'}"
                            >
                                <User class="h-5 w-5" />
                                <span class="font-medium">Contact</span>
                            </button>
                            <button
                                type="button"
                                onclick={() => $form.type = 'organisation'}
                                class="flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-colors {isOrganisation ? 'border-primary bg-primary/10' : 'border-muted hover:border-muted-foreground/50'}"
                            >
                                <Building2 class="h-5 w-5" />
                                <span class="font-medium">Organisation</span>
                            </button>
                        </div>
                    </FormBlock>
                {/if}

                <!-- Dynamic fields from blocks -->
                {#each blocks as block (block.id)}
                    <FormBlock label={block.label}>
                        {#each getSortedFields(block.fields) as field (field.id)}
                            {#if shouldShowField(field)}
                                {@const fieldLabel = getFieldLabel(field)}
                                {@const fieldWithRequired = { ...field, label: fieldLabel, required: isFieldRequired(field) }}
                                {#if field.column_name === 'organisation_id'}
                                    <!-- Related organisation picker -->
                                    <RelatedSingle
                                        label="Belongs to Organisation"
                                        bind:value={$form.organisation_id}
                                        error={$form.errors.organisation_id}
                                        bind:isOpen={isSelectOrgOpen}
                                        bind:valLabel={orgLabel}
                                    >
                                        <CustomerTable
                                            view="related"
                                            typeFilter="organisation"
                                            onRowClick={(row) => {
                                                $form.organisation_id = row?.id ?? null;
                                                orgLabel = row?.name ?? '';
                                                isSelectOrgOpen = false;
                                            }}
                                        />
                                    </RelatedSingle>
                                {:else if field.ui_type === 'select'}
                                    <Select
                                        label={fieldLabel}
                                        bind:value={$form[field.column_name]}
                                        options={parseFieldOptions(field)}
                                        error={$form.errors[field.column_name]}
                                        field={fieldWithRequired}
                                    />
                                {:else if field.ui_type === 'checkbox'}
                                    <Checkbox
                                        bind:checked={$form[field.column_name]}
                                        label={fieldLabel}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'textarea'}
                                    <Textarea
                                        label={fieldLabel}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                        field={fieldWithRequired}
                                    />
                                {:else if field.ui_type === 'datetime'}
                                    <DateTime
                                        label={fieldLabel}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                        field={fieldWithRequired}
                                    />
                                {:else}
                                    <Input
                                        field={fieldWithRequired}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                        onkeydown={() => {}}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <div class="mt-5 flex justify-end gap-2">
                    <Button type="button" variant="outline" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$form.processing}>
                        {#if $form.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}